// App.js
import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  Container,
  Box,
  Center,
  Spinner,
  Flex,
  Heading,
  HStack,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  useMediaQuery,
  useColorModeValue,
  IconButton,
  Img,
  Icon,
  Text,
  Link as ChakraLink,
  SimpleGrid,
  Badge,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { FaUser, FaBars, FaInstagram, FaFacebook } from 'react-icons/fa';
import { ChevronDownIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { getPendingNotificationsCount as getPendingNotificationsCountFromUser } from './utils/pendingNotifications';

import './App.css';
import Account from './components/Account';
import Attivita from './components/Attivita';
import Tesseramento from './components/Tesseramento';
import TesseramentoRapido from './components/TesseramentoRapido';
import EventDetail from './pages/EventDetail';
import EventGuestBookingSuccess from './pages/EventGuestBookingSuccess';
import Home from './components/Home';
import Contact from './components/Contact';
import PaymentResult from './components/PaymentResult';
import MenuDrawer from './components/MenuDrawer';
import RandomBalls from './components/RandomBalls';
import Magic from './components/Magic/Magic';
import { useAuth } from './contexts/AuthContext';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import StaticPage from './pages/StaticPage';
import AboutUs from './pages/AboutUs';
import LaSede from './pages/LaSede';
import SostieniCIL from './pages/Sostienicil';
import Sponsor from './pages/Sponsor';
import Donazioni from './pages/Donazioni';
import Obiettivi from './pages/Obiettivi';
import Patreon from './pages/Patreon';
import CILplus from './pages/CILplus';
import FAQ from './pages/FAQ';
import Merch from './pages/Merch';
import ProductDetail from './pages/ProductDetail';
import MerchOrderSuccess from './pages/MerchOrderSuccess';
import LogoImage from './images/logo.png';
import PageNotFound from './pages/PageNotFound';
import ProgettiInCorso from './pages/ProgettiInCorso';
import ArchivioAttivita from './pages/ArchivioAttivita';
import Turnandre from './pages/Turnandre';
import Maddalene from './pages/Maddalene';
import ScambiCarte from './pages/ScambiCarte';
import Decks from './pages/Decks';
import DeckDetail from './pages/DeckDetail';
import MagicTheGathering from './pages/MagicTheGathering';
import Leaderboards from './pages/Leaderboards';
import Forms from './pages/Forms';
import FormDetail from './pages/FormDetail';
import FB from './pages/FB';
import CardGuessGamePage from './pages/CardGuessGamePage';
import MidiGuessGamePage from './pages/MidiGuessGamePage';
import CilWrapped2025 from './pages/CilWrapped2025';
import Bazar from './pages/Bazar';
import BazarDetail from './pages/BazarDetail';
import BazarNew from './pages/BazarNew';
import BazarEdit from './pages/BazarEdit';
import Gallery from './pages/Gallery';
import CentroEstivo2025Page from './pages/CentroEstivo2025Page';
import Network from './pages/Network';
import ProfileDetail from './pages/ProfileDetail';
import CinquePerMille from './pages/CinquePerMille';
import Logbook from './pages/Logbook';
import LogbookDetail from './pages/LogbookDetail';
import CatalogoPremi from './pages/CatalogoPremi';
import CatalogoPremiSet from './pages/CatalogoPremiSet';
import CilPoints from './pages/CilPoints';
import ManualeGiochi from './pages/ManualeGiochi';
import ManualeGiocoCreate from './pages/ManualeGiocoCreate';
import ManualeGiocoDetail from './pages/ManualeGiocoDetail';
import TargetLandingPage from './pages/TargetLandingPage';
import SchoolProposalsLanding from './pages/SchoolProposalsLanding';
import SchoolProposalRequestForm from './pages/SchoolProposalRequestForm';
import PropostaPubblicaDetailPage from './pages/PropostaPubblicaDetailPage';
import TimebankNew from './pages/TimebankNew';
import {
  resolveProposalTheme,
  resolveProposalThemeKeyFromPath,
} from './components/proposals-standalone/proposalThemes';
import {
  getOtherLandingNavItems,
  getStandaloneLandingTitle,
} from './config/landingPagesConfig';
import { prefetchVirtualSpace } from './features/virtualSpace/prefetch';

// Lazy load per performance
const CentroEstivo2026Page = lazy(() => import('./pages/CentroEstivo2026Page'));
const BoxClickGame = lazy(() => import('./components/BoxClickGame'));
const VirtualSpacePage = lazy(() => import('./pages/VirtualSpacePage'));

// Componente per redirect esterni
const ExternalRedirect = ({ url }) => {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return null;
};

const isStandaloneProposalPath = (pathname = '') => {
  if (!pathname) return false;
  if (pathname === '/proposte-scuole') return true;
  if (pathname === '/richiesta-scuole') return true;
  if (/^\/proposte-scuole\/[^/]+$/.test(pathname)) return true;
  if (/^\/eventi-adulti\/proposte\/[^/]+$/.test(pathname)) return true;
  if (/^\/magic-tcg\/proposte\/[^/]+$/.test(pathname)) return true;
  if (/^\/proposte-famiglie\/proposte\/[^/]+$/.test(pathname)) return true;
  if (/^\/proposte-ragazzi\/proposte\/[^/]+$/.test(pathname)) return true;
  return (
    pathname === '/eventi-adulti' ||
    pathname === '/magic-tcg' ||
    pathname === '/proposte-famiglie' ||
    pathname === '/proposte-ragazzi'
  );
};

/** Stesso form contatti con query (?proposta=slug) da link CTA proposte pubbliche. */
function ContattiQueryAlias() {
  const { search } = useLocation();
  return <Navigate to={`/contattaci${search}`} replace />;
}

function LegacyProductRedirect() {
  const { id } = useParams();
  return <Navigate to={`/merch/product/${id}`} replace />;
}

const App = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // Dynamic colors based on theme
  const headerBg = useColorModeValue('brand.100', 'gray.800'); // Giallo / Grigio scuro
  const textColor = useColorModeValue('black', 'black');
  const headerColor = useColorModeValue('black', 'white'); // Testo header: nero in light, bianco in dark
  // const menuBg = useColorModeValue('white', 'gray.700');
  const buttonBg = useColorModeValue('#ffcc00', '#ffcc00'); // Giallo in light, Blu in dark
  const buttonHoverBg = useColorModeValue('#333333', '#ffcc00'); // Hover inverso
  const buttonHoverColor = useColorModeValue('#ffffff', '#ffffff'); // Hover inverso
  const iconColor = useColorModeValue('black', 'white'); // Icone: nero in light, bianco in dark
  const {
    isAuthenticated,
    userData,
    setIsAuthenticated,
    setUserData,
    loading,
  } = useAuth();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isMenuDrawerOpen,
    onOpen: onDrawerMenuOpen,
    onClose: onDrawerMenuClosed,
  } = useDisclosure();

  const [loginData, setLoginData] = useState({ identifier: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLargerThan600] = useMediaQuery('(min-width: 768px)');
  const [eventsData, setEventsData] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [isSostieniOpen, setIsSostieniOpen] = useState(false);

  const onAuth = (isAuth) => {
    setIsAuthenticated(isAuth);
    if (!isAuth) {
      setUserData(null);
    }
  };

  const onModalClose = () => {
    setLoginData({ identifier: '', password: '' });
    setLoginError('');
    onClose();
  };

  // Helper per calcolare le notifiche pendenti
  const getPendingNotificationsCount = () =>
    getPendingNotificationsCountFromUser(userData);

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginError('');
    try {
      const response = await axios.post('/api/login', loginData);
      if (response.status === 200) {
        onAuth(true);
        setUserData(response.data);
        onModalClose();
      } else {
        setLoginError(response.data?.message || 'Accesso non riuscito');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Accesso non riuscito';
      setLoginError(msg);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || loading || pathname === '/spazio') return undefined;

    const runPrefetch = () => prefetchVirtualSpace();
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(runPrefetch);
      return () => window.cancelIdleCallback(id);
    }
    const timer = window.setTimeout(runPrefetch, 2000);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated, loading, pathname]);

  // Effetto che "ascolta" il pathname
  useEffect(() => {
    if (pathname === '/login') {
      onOpen();
      const redirect = new URLSearchParams(search).get('redirect');
      navigate(redirect || '/', { replace: true });
    }
  }, [pathname, search, navigate, onOpen]);

  useEffect(() => {
    // Scroll window to top whenever route changes
    window.scrollTo(0, 0);
  }, [pathname]);

  // Disabilita lo scroll quando si è sulla pagina del gioco
  useEffect(() => {
    if (pathname === '/wintergame') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup: ripristina lo scroll quando si esce dalla pagina
    return () => {
      document.body.style.overflow = '';
    };
  }, [pathname]);

  const fetchEvents = async ({ withLoading = true } = {}) => {
    if (withLoading) {
      setLoadingEvents(true);
    }
    try {
      const response = await axios.get('/api/eventi');
      setEventsData(response.data.events);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      if (withLoading) {
        setLoadingEvents(false);
      }
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Chiudi i sottomenu quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSostieniOpen && !event.target.closest('[data-sostieni-menu]')) {
        setIsSostieniOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSostieniOpen]);

  // Chiudi i sottomenu quando si passa sopra altre voci del menu
  const closeAllSubmenus = () => {
    setIsSostieniOpen(false);
  };

  const isCentroEstivo =
    pathname.startsWith('/event/centro-estivo-2025') ||
    pathname.startsWith('/event/centro-estivo-2026') ||
    pathname === '/centro-estivo-2025' ||
    pathname === '/centro-estivo-2026';

  const isCentroEstivo2026 =
    pathname.startsWith('/event/centro-estivo-2026') ||
    pathname === '/centro-estivo-2026';

  const isMunlightSonata = pathname.startsWith('/event/munlight-sonata');

  const isGallery = pathname === '/gallery';
  const isVirtualSpace = pathname === '/spazio';
  const isImmersiveFullscreen = isGallery || isVirtualSpace;
  const isStandaloneProposals = isStandaloneProposalPath(pathname);
  const standaloneTheme = resolveProposalTheme(
    resolveProposalThemeKeyFromPath(pathname)
  );
  const otherLandingNavItems = getOtherLandingNavItems(pathname);
  const standaloneLandingTitle = getStandaloneLandingTitle(pathname);
  const standaloneHeaderTitleColor =
    standaloneTheme.colors.onLightTitle ?? standaloneTheme.colors.text;

  return (
    <>
      {isCentroEstivo && <RandomBalls />}
      <Container
        display="flex"
        flexDirection="column"
        maxW={
          isImmersiveFullscreen ||
          isCentroEstivo2026 ||
          isMunlightSonata ||
          isStandaloneProposals
            ? '100%'
            : 'container.xl'
        }
        padding={{
          lg:
            isImmersiveFullscreen || isStandaloneProposals
              ? '0'
              : '112px 0 0 0',
          md:
            isImmersiveFullscreen || isStandaloneProposals
              ? '0'
              : '112px 0 0 0',
          sm:
            isImmersiveFullscreen || isStandaloneProposals ? '0' : '56px 0 0 0',
          base:
            isImmersiveFullscreen || isStandaloneProposals ? '0' : '56px 0 0 0',
        }}
        minHeight="100vh"
        bg="transparent"
        color={textColor}
      >
        {/* Header with Theme Toggle */}
        {!isImmersiveFullscreen && !isStandaloneProposals && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            bg={headerBg}
            px={4}
            py={2}
            boxShadow="md"
            mb={6}
            position="fixed"
            top="0"
            left="50%"
            height="56px"
            transform="translateX(-50%)"
            maxW="container.xl"
            width="100%"
            zIndex={10000}
          >
            {!isLargerThan600 && (
              <Button
                color={iconColor}
                onClick={onDrawerMenuOpen}
                backgroundColor={buttonBg}
                _hover={{ backgroundColor: buttonHoverBg }}
                leftIcon={<FaBars />}
                colorScheme="teal"
                variant="outline"
              >
                Menu
              </Button>
            )}
            <Heading as="h1" size="lg" color={headerColor}>
              <Link to="/">
                <Img
                  src={LogoImage}
                  alt="CIL - Creatività in libertà"
                  height="28px"
                  width="auto"
                  verticalAlign="middle"
                  top="14px"
                  left="50%"
                  transform={{ base: 'translateX(-50%)', md: 'translateX(0)' }}
                  position={{ base: 'absolute', md: 'static' }}
                />
              </Link>
            </Heading>
            <HStack spacing={0}>
              {/* Authentication Buttons */}
              {isAuthenticated ? (
                isLargerThan600 ? (
                  <Button
                    color="gray.700"
                    as={Link}
                    to="/account"
                    backgroundColor={buttonBg}
                    _hover={{
                      backgroundColor: buttonHoverBg,
                      color: buttonHoverColor,
                    }}
                    leftIcon={<FaUser />}
                    colorScheme="teal"
                    bordercolor="gray.600"
                    position="relative"
                  >
                    Il Mio Account
                    {getPendingNotificationsCount() > 0 && (
                      <Badge
                        position="absolute"
                        top="-2px"
                        right="-2px"
                        colorScheme="red"
                        variant="solid"
                        borderRadius="full"
                        minW="18px"
                        h="18px"
                        fontSize="xs"
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        boxShadow="0 2px 4px rgba(0,0,0,0.2)"
                      >
                        {getPendingNotificationsCount()}
                      </Badge>
                    )}
                  </Button>
                ) : (
                  <Box position="relative">
                    <IconButton
                      as={Link}
                      to="/account"
                      color="gray.700"
                      border="1px solid"
                      bordercolor="gray.600"
                      icon={<FaUser />}
                      backgroundColor={buttonBg}
                      _hover={{
                        backgroundColor: buttonHoverBg,
                        color: buttonHoverColor,
                      }}
                    />
                    {getPendingNotificationsCount() > 0 && (
                      <Badge
                        position="absolute"
                        top="-2px"
                        right="-2px"
                        colorScheme="red"
                        variant="solid"
                        borderRadius="full"
                        minW="18px"
                        h="18px"
                        fontSize="xs"
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        boxShadow="0 2px 4px rgba(0,0,0,0.2)"
                      >
                        {getPendingNotificationsCount()}
                      </Badge>
                    )}
                  </Box>
                )
              ) : (
                <Button
                  backgroundColor={buttonBg}
                  _hover={{ backgroundColor: buttonHoverBg, color: 'white' }}
                  onClick={onOpen}
                >
                  Login
                </Button>
              )}
            </HStack>
          </Box>
        )}

        {/* Navigation Menu */}
        {!isImmersiveFullscreen &&
          !isStandaloneProposals &&
          isLargerThan600 && (
            <Box
              display="flex"
              justifyContent="center"
              mb={6}
              width="100%"
              overflow="visible"
              bg={headerBg}
              boxShadow="lg"
              borderBottomLeftRadius="md"
              borderBottomRightRadius="md"
              p={2}
              position="fixed"
              top="56px"
              left="50%"
              transform="translateX(-50%)"
              maxW="container.xl"
              zIndex={9999}
            >
              <HStack spacing={4}>
                <NavLink
                  className="menu-link"
                  to="/"
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? buttonHoverBg : '',
                    cursor: isActive ? 'default' : 'pointer',
                    color: isActive ? buttonHoverColor : textColor,
                    padding: '8px 16px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                  })}
                  onMouseEnter={closeAllSubmenus}
                >
                  Home
                </NavLink>
                <NavLink
                  className="menu-link"
                  to="/attivita"
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? buttonHoverBg : '',
                    cursor: isActive ? 'default' : 'pointer',
                    color: isActive ? buttonHoverColor : textColor,
                    padding: '8px 16px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                  })}
                  onMouseEnter={closeAllSubmenus}
                >
                  Eventi
                </NavLink>
                <Box position="relative" data-sostieni-menu>
                  <Button
                    onClick={() => setIsSostieniOpen(!isSostieniOpen)}
                    backgroundColor="#ffcc00"
                    color="black"
                    padding="8px 16px"
                    borderRadius="6px"
                    rightIcon={<ChevronDownIcon />}
                    variant="ghost"
                    fontWeight="normal"
                    cursor="pointer"
                    _hover={{ bg: '#e6b800' }}
                  >
                    Sostieni CIL
                  </Button>
                  {isSostieniOpen && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      bg="white"
                      boxShadow="xl"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                      zIndex={100000}
                      minW="200px"
                      py={1}
                      mt={1}
                    >
                      <Box
                        as={Link}
                        to="/sostienicil"
                        display="block"
                        px={4}
                        py={2}
                        color={textColor}
                        textDecoration="none"
                        _hover={{
                          backgroundColor: buttonHoverBg,
                          color: buttonHoverColor,
                        }}
                        onClick={() => setIsSostieniOpen(false)}
                      >
                        Dona ora
                      </Box>
                      <Box
                        as={Link}
                        to="/merch"
                        display="block"
                        px={4}
                        py={2}
                        color={textColor}
                        textDecoration="none"
                        _hover={{
                          backgroundColor: buttonHoverBg,
                          color: buttonHoverColor,
                        }}
                        onClick={() => setIsSostieniOpen(false)}
                      >
                        Merch
                      </Box>
                      <Box
                        as={Link}
                        to="/cil-5-per-1000"
                        display="block"
                        px={4}
                        py={2}
                        color={textColor}
                        textDecoration="none"
                        _hover={{
                          backgroundColor: buttonHoverBg,
                          color: buttonHoverColor,
                        }}
                        onClick={() => setIsSostieniOpen(false)}
                      >
                        Dona il 5x1000
                      </Box>
                    </Box>
                  )}
                </Box>
                <NavLink
                  className="menu-link"
                  to="/logbook"
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? buttonHoverBg : '',
                    cursor: isActive ? 'default' : 'pointer',
                    color: isActive ? buttonHoverColor : textColor,
                    padding: '8px 16px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                  })}
                  onMouseEnter={closeAllSubmenus}
                >
                  Logbook
                </NavLink>
                {!isAuthenticated && (
                  <NavLink
                    className="menu-link"
                    to="/tesseramento"
                    style={({ isActive }) => ({
                      backgroundColor: isActive ? buttonHoverBg : '',
                      cursor: isActive ? 'default' : 'pointer',
                      color: isActive ? buttonHoverColor : textColor,
                      padding: '8px 16px',
                      borderRadius: '6px',
                      textDecoration: 'none',
                    })}
                    onMouseEnter={closeAllSubmenus}
                  >
                    Tesseramento
                  </NavLink>
                )}
                <NavLink
                  className="menu-link"
                  to="/contattaci"
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? buttonHoverBg : '',
                    cursor: isActive ? 'default' : 'pointer',
                    color: isActive ? buttonHoverColor : textColor,
                    padding: '8px 16px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                  })}
                  onMouseEnter={closeAllSubmenus}
                >
                  Contattaci
                </NavLink>
              </HStack>
            </Box>
          )}

        {isStandaloneProposals ? (
          <Box
            as="header"
            role="banner"
            py={{ base: 3, md: 4 }}
            px={4}
            w="100%"
            bg="white"
            borderBottomWidth="1px"
            borderBottomColor="blackAlpha.100"
          >
            <Flex
              maxW="container.xl"
              mx="auto"
              w="100%"
              justify="center"
              align="center"
              gap={{ base: 4, md: 6 }}
              flexWrap="wrap"
            >
              <Link
                to="/"
                aria-label="CIL - Torna alla home del sito"
                flexShrink={0}
              >
                <Img
                  src={LogoImage}
                  alt="CIL - Creatività in libertà"
                  h={{ base: '32px', md: '38px' }}
                  w="auto"
                />
              </Link>
              {standaloneLandingTitle ? (
                <Heading
                  as="span"
                  size={{ base: 'sm', md: 'md' }}
                  fontFamily={standaloneTheme.fontHeading}
                  color={standaloneHeaderTitleColor}
                  fontWeight="700"
                  lineHeight="shorter"
                  maxW={{ base: 'min(100%, 16rem)', md: 'min(100%, 28rem)' }}
                  minW={0}
                  noOfLines={2}
                  textAlign="center"
                  pl={{ base: 5, md: 8 }}
                  ml={{ base: 1, md: 2 }}
                  borderLeftWidth="1px"
                  borderLeftColor="blackAlpha.200"
                >
                  {standaloneLandingTitle}
                </Heading>
              ) : null}
            </Flex>
          </Box>
        ) : null}

        {/* Main Content Area - flexShrink 0 + overflow visible: una sola scrollbar (pagina), non scroll interno */}
        <Box
          width="100%"
          flexGrow={1}
          flexShrink={0}
          className="content"
          minHeight="inherit"
          overflow="visible"
        >
          <Routes>
            <Route
              path="/account/*"
              element={
                <Account
                  isAuthenticated={isAuthenticated}
                  userData={userData}
                  onAuth={onAuth}
                />
              }
            />
            <Route
              path="/attivita"
              element={
                <Attivita
                  eventsData={eventsData}
                  fetchEvents={fetchEvents}
                  loadingEvents={loadingEvents}
                />
              }
            />
            <Route
              path="/eventi"
              element={
                <Attivita
                  eventsData={eventsData}
                  fetchEvents={fetchEvents}
                  loadingEvents={loadingEvents}
                />
              }
            />
            <Route
              path="/calendario"
              element={
                <Attivita
                  eventsData={eventsData}
                  fetchEvents={fetchEvents}
                  loadingEvents={loadingEvents}
                />
              }
            />
            <Route
              path="/tesseramento"
              element={
                <Tesseramento
                  isAuthenticated={isAuthenticated}
                  userData={userData}
                />
              }
            />
            <Route
              path="/tesseramento-rapido"
              element={
                isAuthenticated ? (
                  <Navigate to="/account/tesseramenti" replace />
                ) : (
                  <Box py={{ base: 6, md: 12 }} px={4}>
                    <TesseramentoRapido />
                  </Box>
                )
              }
            />
            <Route
              path="/event/:urlTitle/prenotazione-confermata"
              element={<EventGuestBookingSuccess />}
            />
            <Route
              path="/event/:urlTitle"
              element={
                <EventDetail userData={userData} eventsData={eventsData} />
              }
            />
            <Route
              path="/"
              element={
                <Home eventsData={eventsData} loadingEvents={loadingEvents} />
              }
            />
            <Route
              path="/login"
              element={
                <Home eventsData={eventsData} loadingEvents={loadingEvents} />
              }
            />
            <Route path="/contattaci" element={<Contact />} />
            <Route path="/contatti" element={<ContattiQueryAlias />} />
            <Route path="/payment-result" element={<PaymentResult />} />
            <Route path="/password-dimenticata" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/page/:pageId" element={<StaticPage />} />
            <Route
              path="/chi-siamo"
              element={<Navigate to="/page/about-us" replace />}
            />
            <Route
              path="/come-partecipare"
              element={<Navigate to="/tesseramento" replace />}
            />
            <Route path="/page/about-us" element={<AboutUs />} />
            <Route path="/la-sede" element={<LaSede />} />
            <Route path="/sostienicil" element={<SostieniCIL />} />
            <Route path="/sponsor" element={<Sponsor />} />
            <Route path="/merch" element={<Merch />} />
            <Route path="/merch/product/:id" element={<ProductDetail />} />
            <Route
              path="/merch/ordine-confermato"
              element={<MerchOrderSuccess />}
            />
            <Route path="/donazioni" element={<Donazioni />} />
            <Route path="/cil-5-per-1000" element={<CinquePerMille />} />
            <Route path="/obiettivi" element={<Obiettivi />} />
            <Route path="/patreon" element={<Patreon />} />
            <Route path="/cilplus" element={<CILplus />} />
            <Route path="/domande-frequenti" element={<FAQ />} />

            <Route path="/progetti/in-corso" element={<ProgettiInCorso />} />
            <Route path="/archivio-2025" element={<ArchivioAttivita />} />
            <Route path="/turnandre" element={<Turnandre />} />
            <Route path="/maddalene" element={<Maddalene />} />

            <Route path="/magic" element={<Magic />} />
            <Route path="/magicthegathering" element={<MagicTheGathering />} />
            <Route
              path="/leaderboards"
              element={<Leaderboards eventsData={eventsData} />}
            />
            <Route path="/scambi-carte" element={<ScambiCarte />} />
            <Route path="/bazar" element={<Bazar />} />
            <Route path="/bazar/nuovo" element={<BazarNew />} />
            <Route path="/bazar/:id" element={<BazarDetail />} />
            <Route path="/bazar/:id/modifica" element={<BazarEdit />} />
            <Route path="/decks" element={<Decks eventsData={eventsData} />} />
            <Route path="/decks/:deckId" element={<DeckDetail />} />
            <Route
              path="/products"
              element={<Navigate to="/merch" replace />}
            />
            <Route path="/products/:id" element={<LegacyProductRedirect />} />
            <Route path="/forms" element={<Forms />} />
            <Route path="/forms/:documentId" element={<FormDetail />} />
            <Route path="/feb" element={<FB />} />
            <Route
              path="/wintergame"
              element={
                <Suspense fallback={null}>
                  <BoxClickGame isActive onClose={() => navigate('/')} />
                </Suspense>
              }
            />
            <Route path="/card-guess-game" element={<CardGuessGamePage />} />
            <Route path="/cil-wrapped-2025" element={<CilWrapped2025 />} />
            <Route path="/midi-guess-game" element={<MidiGuessGamePage />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route
              path="/spazio"
              element={
                <Suspense
                  fallback={
                    <Center minH="100vh">
                      <Spinner size="xl" />
                    </Center>
                  }
                >
                  <VirtualSpacePage />
                </Suspense>
              }
            />
            <Route
              path="/centro-estivo"
              element={<Navigate to="/event/centro-estivo" replace />}
            />
            <Route
              path="/centro-estivo-2025"
              element={<CentroEstivo2025Page />}
            />
            <Route
              path="/centro-estivo-2026"
              element={
                <Suspense fallback={null}>
                  <CentroEstivo2026Page />
                </Suspense>
              }
            />
            <Route path="/network" element={<Network />} />
            <Route path="/network/:documentId" element={<ProfileDetail />} />
            <Route path="/logbook" element={<Logbook />} />
            <Route path="/logbook/:slug" element={<LogbookDetail />} />
            <Route path="/catalogo-premi" element={<CatalogoPremi />} />
            <Route
              path="/catalogo-premi/:slug"
              element={<CatalogoPremiSet />}
            />
            <Route path="/cil-points" element={<CilPoints />} />
            <Route path="/manuale-giochi" element={<ManualeGiochi />} />
            <Route
              path="/manuale-giochi/nuovo"
              element={<ManualeGiocoCreate />}
            />
            <Route
              path="/manuale-giochi/:slug"
              element={<ManualeGiocoDetail />}
            />
            <Route path="/eventi-adulti" element={<TargetLandingPage />} />
            <Route
              path="/eventi-adulti/proposte/:slug"
              element={<PropostaPubblicaDetailPage />}
            />
            <Route path="/magic-tcg" element={<TargetLandingPage />} />
            <Route
              path="/magic-tcg/proposte/:slug"
              element={<PropostaPubblicaDetailPage />}
            />
            <Route
              path="/proposte-scuole"
              element={<SchoolProposalsLanding />}
            />
            <Route
              path="/proposte-scuole/:slug"
              element={<PropostaPubblicaDetailPage />}
            />
            <Route path="/proposte-famiglie" element={<TargetLandingPage />} />
            <Route
              path="/proposte-famiglie/proposte/:slug"
              element={<PropostaPubblicaDetailPage />}
            />
            <Route path="/proposte-ragazzi" element={<TargetLandingPage />} />
            <Route
              path="/proposte-ragazzi/proposte/:slug"
              element={<PropostaPubblicaDetailPage />}
            />
            <Route
              path="/richiesta-scuole"
              element={<SchoolProposalRequestForm />}
            />
            <Route path="/banca-ore/nuovo" element={<TimebankNew />} />
            <Route
              path="/centro-estivo-2024-munlab"
              element={
                <ExternalRedirect url="https://www.cilassociazione.it/event/giornate-dinverno-al-munlab" />
              }
            />

            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Box>
        {/* FOOTER */}
        {!isImmersiveFullscreen && !isStandaloneProposals && (
          <Box
            bg="gray.800"
            color="white"
            py={6}
            position="relative"
            zIndex={10}
          >
            <Container maxW="container.lg">
              {/* 4 colonne, tutte centrate */}
              <SimpleGrid
                columns={{ base: 2, lg: 4 }}
                spacing={{ base: 6, lg: 8 }}
                justifyItems="center" // Centra i box orizzontalmente
                alignItems="center" // Centra il contenuto verticalmente
                textAlign="center" // Testo centrato
              >
                {/* 3. Colonna: Naviga */}
                <Box>
                  <Heading as="h3" size="sm" mb={2} textTransform="uppercase">
                    Naviga
                  </Heading>
                  <Text>
                    <ChakraLink
                      as={Link}
                      to="/page/about-us"
                      textDecoration="underline"
                    >
                      Chi Siamo
                    </ChakraLink>
                  </Text>
                  <Text>
                    <ChakraLink
                      as={Link}
                      to="/la-sede"
                      textDecoration="underline"
                    >
                      La Sede
                    </ChakraLink>
                  </Text>
                  <Text>
                    <ChakraLink
                      as={Link}
                      to="/merch"
                      textDecoration="underline"
                    >
                      Merch
                    </ChakraLink>
                  </Text>
                  <Text>
                    <ChakraLink
                      as={Link}
                      to="/turnandre"
                      textDecoration="underline"
                    >
                      Turnandrè
                    </ChakraLink>
                  </Text>
                  <Text>
                    <ChakraLink
                      as={Link}
                      to="/maddalene"
                      textDecoration="underline"
                    >
                      Casetta delle Madda
                    </ChakraLink>
                  </Text>
                </Box>

                {/* 4. Colonna: Link a Privacy e Termini */}
                <Box>
                  <Heading as="h3" size="sm" mb={2} textTransform="uppercase">
                    Risorse Utili
                  </Heading>
                  <Text>
                    <ChakraLink
                      href="/domande-frequenti"
                      textDecoration="underline"
                    >
                      F.A.Q.
                    </ChakraLink>
                  </Text>
                  <Text>
                    <ChakraLink
                      href="/page/privacy-policy"
                      textDecoration="underline"
                    >
                      Privacy Policy
                    </ChakraLink>
                  </Text>
                  <Text>
                    <ChakraLink
                      href="/page/termini-e-condizioni"
                      textDecoration="underline"
                    >
                      Termini e Condizioni
                    </ChakraLink>
                  </Text>
                </Box>
                {/* 1. Colonna: Info CIL */}
                <Box>
                  <Heading as="h3" size="sm" mb={2} textTransform="uppercase">
                    Contatti
                  </Heading>
                  <Text fontSize="sm">© {new Date().getFullYear()} CIL</Text>
                  <Text fontSize="sm">info@cilassociazione.it</Text>
                  <Text fontSize="sm">+39 353 468 2493</Text>
                  <Text fontSize="sm">Via Carlo Alberto 7 Chieri</Text>
                </Box>

                {/* 2. Colonna: Icone Social */}
                <Box>
                  <Heading as="h3" size="sm" mb={2} textTransform="uppercase">
                    Seguici su
                  </Heading>
                  <HStack spacing={4} justify="center">
                    <ChakraLink
                      href="https://www.facebook.com/people/CIL-Creativit%C3%A0-in-Libert%C3%A0/100091128373973/"
                      isExternal
                    >
                      <Icon as={FaFacebook} w={5} h={5} />
                    </ChakraLink>
                    <ChakraLink
                      href="https://www.instagram.com/cil_associazione/"
                      isExternal
                    >
                      <Icon as={FaInstagram} w={5} h={5} />
                    </ChakraLink>
                  </HStack>
                </Box>
              </SimpleGrid>
            </Container>
          </Box>
        )}

        {isStandaloneProposals && (
          <Box
            bg={standaloneTheme.colors.heroStart}
            color="white"
            py={6}
            px={4}
          >
            <Container maxW="container.lg">
              <HStack
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                flexDirection={{ base: 'column', md: 'row' }}
                spacing={4}
                w="100%"
              >
                <Wrap
                  role="navigation"
                  aria-label="Altre landing proposte"
                  spacing={3}
                  rowGap={2}
                  shouldWrapChildren
                  flex="1"
                  minW={0}
                >
                  {otherLandingNavItems.map((item) => (
                    <WrapItem key={item.path}>
                      <ChakraLink
                        as={Link}
                        to={item.path}
                        fontSize="sm"
                        fontWeight="600"
                        color="whiteAlpha.900"
                        whiteSpace="nowrap"
                        _hover={{
                          color: 'white',
                          textDecoration: 'underline',
                        }}
                      >
                        {item.title}
                      </ChakraLink>
                    </WrapItem>
                  ))}
                </Wrap>
                <Button
                  as={Link}
                  to="/"
                  flexShrink={0}
                  bg={standaloneTheme.colors.accent}
                  color={standaloneTheme.colors.text}
                  _hover={{ opacity: 0.92 }}
                >
                  Vai a cilassociazione.it
                </Button>
              </HStack>
            </Container>
          </Box>
        )}

        {/* DRAWER per menu */}

        {!isImmersiveFullscreen &&
          !isStandaloneProposals &&
          !isLargerThan600 && (
            <MenuDrawer
              isOpen={isMenuDrawerOpen}
              placement="left"
              onModalClose={onDrawerMenuClosed}
              isAuthenticated={isAuthenticated}
            />
          )}

        {/* Yellow Centered Login Modal */}
        {isOpen && (
          <Modal
            isOpen={isOpen}
            onClose={onModalClose}
            isCentered
            data-testid="login-modal"
          >
            <ModalOverlay />
            <form onSubmit={handleLoginSubmit}>
              <ModalContent
                maxW={{ base: '93%', sm: '380px' }}
                borderRadius="xl"
                overflow="hidden"
              >
                <ModalHeader bg="brand.100" color="black" py={4} px={6}>
                  Entra in CIL
                </ModalHeader>
                <ModalCloseButton variant="custom" top={4} color="white" />
                <ModalBody py={6} px={6}>
                  {loginError && (
                    <Alert status="error" mb={4}>
                      <AlertIcon />
                      {loginError}
                    </Alert>
                  )}
                  <FormControl mb={4}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      data-testid="login-email-input"
                      value={loginData.identifier}
                      onChange={(e) =>
                        setLoginData({
                          ...loginData,
                          identifier: e.target.value,
                        })
                      }
                      required
                    />
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel>Password</FormLabel>
                    <Input
                      name="password"
                      type="password"
                      data-testid="login-password-input"
                      value={loginData.password}
                      onChange={(e) =>
                        setLoginData({ ...loginData, password: e.target.value })
                      }
                      required
                    />
                  </FormControl>
                  {/* Forgot Password Link */}
                  <Box textAlign="right" mt={-2}>
                    <Link
                      to="/password-dimenticata"
                      style={{ color: '#36a9e1', fontWeight: 'normal' }}
                      onClick={onModalClose}
                    >
                      Ripristina la password
                    </Link>
                  </Box>
                </ModalBody>
                <ModalFooter py={4} px={6} justifyContent="space-between">
                  <Link
                    to="/tesseramento"
                    style={{ color: '#36a9e1', fontWeight: 'normal' }}
                    onClick={onModalClose}
                  >
                    Diventa Socio
                  </Link>
                  <Box>
                    <Button variant="outline" mr={3} onClick={onClose}>
                      Chiudi
                    </Button>
                    <Button
                      bg="brand.100"
                      type="submit"
                      data-testid="login-submit-button"
                    >
                      Accedi
                    </Button>
                  </Box>
                </ModalFooter>
              </ModalContent>
            </form>
          </Modal>
        )}
      </Container>
    </>
  );
};

export default App;
